/**
 * Auto-answer adapter — maps "what MTTR already knows about this box"
 * into pre-selected answers for the troubleshooting wizard's decision
 * nodes.
 *
 * For each `AutoAnswerKey` declared in flowchart.ts we produce:
 *   - a suggested branch label (must match one of the decision's branches)
 *   - a short "source" string the UI shows so the operator knows *why*
 *     it's pre-selected (e.g., "Tier 3 fresh but Tier 1+2 silent").
 *
 * Operator can always override; the auto-answer is a hint, not a lock.
 *
 * v2 wizard has exactly one auto-answer key: `mqtt_publish_recent`,
 * which derives from the Box payload's publish_chain_broken flag (set
 * by mttr/map_export.py when Tier 3 of the cascade is fresh but
 * Tier 1+2 are silent past the configured freshness window).
 */
import type { AutoAnswerKey } from "./flowchart";
import type { Box } from "../../types/boxes";

export interface AutoAnswer {
  /** Branch label as defined in the matching DecisionNode. */
  suggestedLabel: string;
  /** Short explanation shown to the operator under the auto-answer. */
  source: string;
}

/** Mirrors the seeded default of MttrSettings.MqttPublishRecentMinutes
 *  (migration 056). When map_export starts threading the live snapshot
 *  value into the wizard, this fallback can drop away. */
const DEFAULT_RECENT_MINUTES = 30;

export function deriveAutoAnswer(
  key: AutoAnswerKey,
  box: Box,
): AutoAnswer | null {
  switch (key) {
    case "mqtt_publish_recent": {
      const recentMin = DEFAULT_RECENT_MINUTES;
      if (box.publish_chain_broken === true) {
        const brokerHint = box.edge_mqtt_broker_address
          ? `; edge broker configured as ${box.edge_mqtt_broker_address}`
          : "";
        return {
          suggestedLabel: "NO — publish chain looks broken",
          source: `Tier 3 fresh but Tier 1+2 silent past ${recentMin}m${brokerHint}`,
        };
      }
      // Central cascade is winning — at least one of camera/rfid is
      // resolving via v2_camera_health / v2_mongo_scan / mongo, which
      // means the broker chain IS delivering. Suggest YES.
      const cam = box.camera_signal_source;
      const rfid = box.rfid_signal_source;
      const centralCam = cam === "v2_camera_health" || cam === "mongo";
      const centralRfid = rfid === "v2_mongo_scan" || rfid === "mongo";
      if (centralCam || centralRfid) {
        return {
          suggestedLabel: "YES — messages are flowing",
          source: `Central cascade fresh (camera=${cam ?? "none"}, rfid=${rfid ?? "none"})`,
        };
      }
      // No central signal AND publish_chain_broken=false — usually
      // means the box is genuinely offline (tailscale down, stacked,
      // or never onboarded). Don't suggest either branch.
      return null;
    }
  }
}
